import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStores, createStore, updateStore, deleteStore } from '../api/client';

const StoreContext = createContext();

export const useStores = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch stores from API
  const fetchStores = async () => {
    try {
      setLoading(true);
      setError(null);
      const storesData = await getStores();
      setStores(storesData || []);
    } catch (err) {
      console.error('Error fetching stores:', err);
      setError('Error al cargar las tiendas: ' + (err.response?.data?.detail || err.message));
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  // Create a new store
  const createStoreSync = async (storeData) => {
    try {
      const newStore = await createStore(storeData);
      setStores(prevStores => [...prevStores, newStore]);
      return newStore;
    } catch (err) {
      console.error('Error creating store:', err);
      throw err;
    }
  };

  // Update an existing store
  const updateStoreSync = async (storeId, storeData) => {
    try {
      const updatedStore = await updateStore(storeId, storeData);
      setStores(prevStores => 
        prevStores.map(store => 
          store.id === storeId ? updatedStore : store
        )
      );
      return updatedStore;
    } catch (err) {
      console.error('Error updating store:', err);
      throw err;
    }
  };

  // Delete a store
  const deleteStoreSync = async (storeId) => {
    try {
      await deleteStore(storeId);
      setStores(prevStores => 
        prevStores.filter(store => store.id !== storeId)
      );
    } catch (err) {
      console.error('Error deleting store:', err);
      
      // If store was already deleted (404), just remove it from state
      if (err.response && err.response.status === 404) {
        console.log('Store was already deleted, removing from state...');
        setStores(prevStores => 
          prevStores.filter(store => store.id !== storeId)
        );
        return;
      }
      throw err;
    }
  };

  // Get a specific store by ID
  const getStoreById = (storeId) => {
    return stores.find(store => store.id === storeId);
  };

  // Get store name by ID
  const getStoreName = (storeId) => {
    const store = getStoreById(storeId);
    return store ? store.name : 'Tienda no encontrada';
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchStores();
  }, []);

  const value = {
    stores,
    loading,
    error,
    setError,
    fetchStores,
    createStore: createStoreSync,
    updateStore: updateStoreSync,
    deleteStore: deleteStoreSync,
    getStoreById,
    getStoreName,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};